import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ButtonNew from "../../components/Core/Button/ButtonNew";
import CheckboxNew from "../../components/Core/Checkbox/CheckboxNew";
import CustomerAutoComplete from "../../components/Core/AutoComplete/CustomerAutoComplete";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import Customer from "../../models/Customer";
import { NotificationSettings } from "../../models/UserCustomers";
import { NOTIFICATION_TYPES, NotificationTypeLabel } from "./UserCustomerRow";

interface SelectCustomerDialogProps {
	open: boolean;
	onCancel: () => void;
	onConfirmation: (customer: Customer, notificationSetting: NotificationSettings) => void;
	/** Customer ids already linked to the user — excluded from the picker. */
	selectedCustomers: string[];
	isLoading?: boolean;
}

const DEFAULT_NOTIFICATION_SETTING: NotificationSettings = {
	jobConfirmation: false,
	timeReportSummery: false,
	jobReport: false,
};

/**
 * Dialog for linking a customer to a customer user — pick a customer and
 * choose which notifications the user should receive for it.
 */
const SelectCustomerDialog: React.FC<SelectCustomerDialogProps> = ({
	open,
	onCancel,
	onConfirmation,
	selectedCustomers,
	isLoading,
}) => {
	const { t } = useTranslation();

	const [customer, setCustomer] = useState<Customer | undefined>();
	const [notificationSetting, setNotificationSetting] = useState<NotificationSettings>(
		DEFAULT_NOTIFICATION_SETTING
	);
	const [showEmptyCustomerError, setShowEmptyCustomerError] = useState(false);

	// Start from a clean slate every time the dialog is opened
	useEffect(() => {
		if (open) {
			setCustomer(undefined);
			setNotificationSetting(DEFAULT_NOTIFICATION_SETTING);
			setShowEmptyCustomerError(false);
		}
	}, [open]);

	const toggleNotification = (key: keyof NotificationSettings) => (checked: boolean) =>
		setNotificationSetting((current) => ({ ...current, [key]: checked }));

	function handleConfirm() {
		if (!customer) {
			setShowEmptyCustomerError(true);
			return;
		}
		onConfirmation(customer, notificationSetting);
	}

	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>{t("SELECT_CUSTOMER")}</DialogTitle>
				</DialogHeader>

				<div>
					<CustomerAutoComplete
						autoFocus
						width="100%"
						hidePrefix
						value={customer?.id}
						onChange={(_, selected) => {
							setCustomer(selected);
							if (selected) setShowEmptyCustomerError(false);
						}}
						filter={(c) => !selectedCustomers.includes(c.id)}
					/>
					{showEmptyCustomerError && (
						<p className="mt-1.5 text-xs text-red-600">
							{t("customers.multipleCustomers.errors.emptyCustomer")}
						</p>
					)}
				</div>

				<div>
					<p className="mb-3 text-sm font-semibold text-gray-700">
						{t("customers.multipleCustomers.notificationSettingsTitle")}
					</p>
					<div className="flex flex-col gap-3">
						{NOTIFICATION_TYPES.map(({ key, labelKey, Icon }) => (
							<CheckboxNew
								key={key}
								checked={!!notificationSetting[key]}
								onCheckedChange={toggleNotification(key)}
								label={<NotificationTypeLabel labelKey={labelKey} Icon={Icon} />}
							/>
						))}
					</div>
				</div>

				<DialogFooter className="mt-2">
					<ButtonNew variant="neutral" onClick={onCancel}>
						{t("customers.multipleCustomers.cancel")}
					</ButtonNew>
					<ButtonNew onClick={handleConfirm} disabled={isLoading}>
						{t("customers.multipleCustomers.addCustomer")}
					</ButtonNew>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default SelectCustomerDialog;
